// Platform-wide toast copy overrides.
//
// Every toast's DEFAULT text lives at its call site (and is catalogued in
// lib/messages.js for the Superadmin editor). This module lets a superadmin
// re-word any message without a deploy: it wraps sonner's shared `toast` object
// IN PLACE, so all ~340 existing `toast.success(...) / .error(...)` calls across
// the app are covered with zero changes to call sites. When a custom string
// exists for a message's exact default, we swap it in; otherwise the default
// shows unchanged.
import { toast } from 'sonner';

let overrides = {};

const resolve = (msg) =>
  (typeof msg === 'string' && Object.prototype.hasOwnProperty.call(overrides, msg))
    ? overrides[msg]
    : msg;

// Wrap the message-producing methods once. Each keeps sonner's original
// behaviour (options, promise handling, etc.) — only the message string changes.
['success', 'error', 'info', 'warning', 'message', 'loading'].forEach((k) => {
  const original = toast[k];
  if (typeof original === 'function') {
    toast[k] = (msg, opts) => original(resolve(msg), opts);
  }
});

// Fetch the override map (public, cheap) and apply it. Safe to call anytime and
// safe to fail — this is cosmetic, so on any error the built-in defaults show.
export const loadToastOverrides = async () => {
  try {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${base}/api/ui-messages`, { headers: { Accept: 'application/json' } });
    if (res.ok) {
      const map = await res.json();
      if (map && typeof map === 'object') overrides = map;
    }
  } catch (e) {
    /* cosmetic only — ignore */
  }
};

// Let the Superadmin editor apply edits live (so a test toast reflects a change
// before it's even saved / without a page reload).
export const setToastOverrides = (map) => { overrides = (map && typeof map === 'object') ? map : {}; };
export const getToastOverrides = () => overrides;
